import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';

export const TIMEZONE_INFO: Record<
  string,
  {
    standard: { name: string; abbreviation: string; utcOffset: string };
    daylight: { name: string; abbreviation: string; utcOffset: string };
  }
> = {
  'America/Los_Angeles': {
    standard: {
      name: 'Pacific Standard Time',
      abbreviation: 'PST',
      utcOffset: '-8',
    },
    daylight: {
      name: 'Pacific Daylight Time',
      abbreviation: 'PDT',
      utcOffset: '-7',
    },
  },
  'America/New_York': {
    standard: {
      name: 'Eastern Standard Time',
      abbreviation: 'EST',
      utcOffset: '-5',
    },
    daylight: {
      name: 'Eastern Daylight Time',
      abbreviation: 'EDT',
      utcOffset: '-4',
    },
  },
  'Europe/London': {
    standard: {
      name: 'Greenwich Mean Time',
      abbreviation: 'GMT',
      utcOffset: '+0',
    },
    daylight: {
      name: 'British Summer Time',
      abbreviation: 'BST',
      utcOffset: '+1',
    },
  },
  'Europe/Paris': {
    standard: {
      name: 'Central European Time',
      abbreviation: 'CET',
      utcOffset: '+1',
    },
    daylight: {
      name: 'Central European Summer Time',
      abbreviation: 'CEST',
      utcOffset: '+2',
    },
  },
  'America/Sao_Paulo': {
    standard: {
      name: 'Brasilia Standard Time',
      abbreviation: 'BRT',
      utcOffset: '-3',
    },
    daylight: {
      name: 'Brasilia Summer Time',
      abbreviation: 'BRST',
      utcOffset: '-2',
    },
  },
  'Asia/Tokyo': {
    standard: {
      name: 'Japan Standard Time',
      abbreviation: 'JST',
      utcOffset: '+9',
    },
    daylight: {
      name: 'Japan Standard Time',
      abbreviation: 'JST',
      utcOffset: '+9',
    },
  },
  'Asia/Shanghai': {
    standard: {
      name: 'China Standard Time',
      abbreviation: 'CST',
      utcOffset: '+8',
    },
    daylight: {
      name: 'China Standard Time',
      abbreviation: 'CST',
      utcOffset: '+8',
    },
  },
  'Asia/Singapore': {
    standard: {
      name: 'Singapore Standard Time',
      abbreviation: 'SST',
      utcOffset: '+8',
    },
    daylight: {
      name: 'Singapore Standard Time',
      abbreviation: 'SST',
      utcOffset: '+8',
    },
  },
  'Australia/Perth': {
    standard: {
      name: 'Australian Western Standard Time',
      abbreviation: 'AWST',
      utcOffset: '+8',
    },
    daylight: {
      name: 'Australian Western Standard Time',
      abbreviation: 'AWST',
      utcOffset: '+8',
    },
  },
  'Australia/Sydney': {
    standard: {
      name: 'Australian Eastern Standard Time',
      abbreviation: 'AEST',
      utcOffset: '+10',
    },
    daylight: {
      name: 'Australian Eastern Daylight Time',
      abbreviation: 'AEDT',
      utcOffset: '+11',
    },
  },
  'Pacific/Norfolk': {
    standard: {
      name: 'Norfolk Island Standard Time',
      abbreviation: 'NFT',
      utcOffset: '+11',
    },
    daylight: {
      name: 'Norfolk Island Daylight Time',
      abbreviation: 'NFDT',
      utcOffset: '+12',
    },
  },
  'Asia/Kolkata': {
    standard: {
      name: 'India Standard Time',
      abbreviation: 'IST',
      utcOffset: '+5:30',
    },
    daylight: {
      name: 'India Standard Time',
      abbreviation: 'IST',
      utcOffset: '+5:30',
    },
  },
  'Europe/Istanbul': {
    standard: { name: 'Turkey Time', abbreviation: 'TRT', utcOffset: '+3' },
    daylight: { name: 'Turkey Time', abbreviation: 'TRT', utcOffset: '+3' },
  },
  'Asia/Riyadh': {
    standard: {
      name: 'Arabian Standard Time',
      abbreviation: 'AST',
      utcOffset: '+3',
    },
    daylight: {
      name: 'Arabian Standard Time',
      abbreviation: 'AST',
      utcOffset: '+3',
    },
  },
  'Europe/Athens': {
    standard: {
      name: 'Eastern European Time',
      abbreviation: 'EET',
      utcOffset: '+2',
    },
    daylight: {
      name: 'Eastern European Summer Time',
      abbreviation: 'EEST',
      utcOffset: '+3',
    },
  },
  'Asia/Dubai': {
    standard: {
      name: 'Gulf Standard Time',
      abbreviation: 'GST',
      utcOffset: '+4',
    },
    daylight: {
      name: 'Gulf Standard Time',
      abbreviation: 'GST',
      utcOffset: '+4',
    },
  },
  'America/Chicago': {
    standard: {
      name: 'Central Standard Time',
      abbreviation: 'CST',
      utcOffset: '-6',
    },
    daylight: {
      name: 'Central Daylight Time',
      abbreviation: 'CDT',
      utcOffset: '-5',
    },
  },
  'America/Mexico_City': {
    standard: {
      name: 'Central Standard Time (Mexico)',
      abbreviation: 'CST-MEX',
      utcOffset: '-6',
    },
    daylight: {
      name: 'Central Standard Time (Mexico)',
      abbreviation: 'CST-MEX',
      utcOffset: '-6',
    },
  },
};

export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

export const COMMON_TIMEZONES = [
  DEFAULT_TIMEZONE,
  'America/New_York',
  'America/Chicago',
  'Europe/London',
  'Europe/Paris',
  'America/Sao_Paulo',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Australia/Perth',
  'Australia/Sydney',
  'Pacific/Norfolk',
  'Asia/Kolkata',
  'Europe/Istanbul',
  'Asia/Riyadh',
  'Europe/Athens',
  'Asia/Dubai',
  'America/Mexico_City',
];

export function getUtcOffset(timezone: string): string {
  return TIMEZONE_INFO[timezone]?.standard.utcOffset || '';
}

export function getCurrentTimezoneInfo(timezone: string = DEFAULT_TIMEZONE): {
  utcOffset: string;
  abbreviation: string;
  isDST: boolean;
} {
  let timezoneData = TIMEZONE_INFO[timezone];
  if (!timezoneData) {
    timezone = DEFAULT_TIMEZONE;
    timezoneData = TIMEZONE_INFO[DEFAULT_TIMEZONE];
  }

  const now = new Date();

  const currentOffset = getTimezoneOffset(timezone, now);
  const januaryOffset = getTimezoneOffset(
    timezone,
    new Date(now.getFullYear(), 0, 1),
  );
  const julyOffset = getTimezoneOffset(
    timezone,
    new Date(now.getFullYear(), 6, 1),
  );

  const standardOffset = Math.min(januaryOffset, julyOffset);
  const isDST =
    januaryOffset !== julyOffset && currentOffset !== standardOffset;

  const utcOffset = formatInTimeZone(now, timezone, 'xxx');
  const { abbreviation } = isDST
    ? timezoneData.daylight
    : timezoneData.standard;

  return { utcOffset, abbreviation, isDST };
}

/**
 * Formats a timezone name with the current UTC offset
 */
export function formatTimezoneName(timezone: string): string {
  const effectiveTimezone = timezone || DEFAULT_TIMEZONE;

  const timezoneData = TIMEZONE_INFO[effectiveTimezone];
  if (!timezoneData) return effectiveTimezone;

  const { utcOffset, isDST } = getCurrentTimezoneInfo(effectiveTimezone);
  const timezoneName = isDST
    ? timezoneData.daylight.name
    : timezoneData.standard.name;

  return `${timezoneName} (UTC ${utcOffset})`;
}

export function formatTimezoneAbbreviation(timezone: string): string {
  const { abbreviation } = getCurrentTimezoneInfo(timezone || DEFAULT_TIMEZONE);
  return abbreviation;
}
